// Timeline.tsx
import { useRef, useState, useEffect } from "react";

interface TimelineProps {
  duration: number;
  currentTime: number;
  trimStart: number;
  trimEnd: number;
  onTrimStartChange: (time: number) => void;
  onTrimEndChange: (time: number) => void;
  onSeek: (time: number) => void;
}

export const Timeline = ({
  duration,
  currentTime,
  trimStart,
  trimEnd,
  onTrimStartChange,
  onTrimEndChange,
  onSeek,
}: TimelineProps) => {
  const timelineRef = useRef<HTMLDivElement>(null);
  const [isDraggingStart, setIsDraggingStart] = useState(false);
  const [isDraggingEnd, setIsDraggingEnd] = useState(false);
  const [isDraggingProgress, setIsDraggingProgress] = useState(false);

  const getTimeFromPosition = (clientX: number) => {
    if (!timelineRef.current) return 0;
    const rect = timelineRef.current.getBoundingClientRect();
    const percentage = Math.max(
      0,
      Math.min(1, (clientX - rect.left) / rect.width)
    );
    return percentage * duration;
  };

  const handleMouseMove = (e: MouseEvent) => {
    const time = getTimeFromPosition(e.clientX);

    if (isDraggingStart) {
      onTrimStartChange(Math.min(time, trimEnd - 0.1));
    } else if (isDraggingEnd) {
      onTrimEndChange(Math.max(time, trimStart + 0.1));
    } else if (isDraggingProgress) {
      onSeek(time);
    }
  };

  const handleMouseUp = () => {
    setIsDraggingStart(false);
    setIsDraggingEnd(false);
    setIsDraggingProgress(false);
  };

  useEffect(() => {
    if (isDraggingStart || isDraggingEnd || isDraggingProgress) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [
    isDraggingStart,
    isDraggingEnd,
    isDraggingProgress,
    trimStart,
    trimEnd,
    duration,
    onTrimStartChange,
    onTrimEndChange,
    onSeek,
  ]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="w-full space-y-3">
      <div className="flex items-center justify-between text-sm text-dark-grey">
        <div className="flex items-center gap-2">
          <span className="text-[16px] leading-[24px] text-site-black inline-block">
            Trim: {formatTime(trimStart)} - {formatTime(trimEnd)}
          </span>
        </div>
        <span className="text-[16px] leading-[24px] text-site-black inline-block">
          Duration: {formatTime(trimEnd - trimStart)}
        </span>
      </div>

      <div
        ref={timelineRef}
        className="relative h-3 bg-light-grey rounded-lg cursor-pointer"
        onMouseDown={(e: any) => {
          const time = getTimeFromPosition(e.clientX);
          onSeek(time);
          setIsDraggingProgress(true);
        }}
      >
        {/* Full timeline track */}
        <div className="absolute inset-0 bg-medium-grey rounded-lg opacity-50" />

        {/* Trimmed section */}
        <div
          className="absolute top-0 bottom-0 bg-site-yellow/30 border-s-2 border-e-2 border-site-yellow"
          style={{
            left: `${(trimStart / duration) * 100}%`,
            width: `${((trimEnd - trimStart) / duration) * 100}%`,
          }}
        />

        {/* Current time indicator */}
        <div
          className="absolute top-0 bottom-0 w-0.5 bg-site-black shadow-lg"
          style={{ left: `${(currentTime / duration) * 100}%` }}
        />

        {/* Start handle */}
        <div
          className="absolute top-1/2 -translate-y-1/2 w-1.5 h-7 bg-site-yellow rounded cursor-ew-resize hover:scale-110 transition-transform shadow-lg"
          style={{ left: `${(trimStart / duration) * 100}%` }}
          onMouseDown={(e) => {
            e.stopPropagation();
            setIsDraggingStart(true);
          }}
        />

        {/* End handle */}
        <div
          className="absolute top-1/2 -translate-y-1/2 w-1.5 h-7 bg-site-yellow rounded cursor-ew-resize hover:scale-110 transition-transform shadow-lg"
          style={{ left: `${(trimEnd / duration) * 100}%` }}
          onMouseDown={(e) => {
            e.stopPropagation();
            setIsDraggingEnd(true);
          }}
        />
      </div>

      <div className="flex justify-between text-xs text-dark-grey">
        <span className="text-[14px] leading-[21px] text-site-black inline-block">
          {formatTime(0)}
        </span>
        <span className="text-[14px] leading-[21px] text-site-black inline-block">
          {formatTime(duration)}
        </span>
      </div>
    </div>
  );
};
